import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import {
  Book as BookIcon,
  Description as ResourceIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import type { Book, Resource } from '../../types/dashboard';
import { logger } from '../../utils/logger';
import { BookDetail } from '../../components/BookDetail';
import { ResourceViewer } from '../../components/ResourceViewer';
import { resourceService } from '../../services/resourceService';
import type { Resource as ResourceType } from '../../types';


interface PersonalizedRecommendationsProps {
  recommendedBooks: Book[];
  recommendedResources: Resource[];
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString();
};

const getCategoryColor = (categoryName?: string) => {
  if (!categoryName) return 'default';
  
  const colors = ['primary', 'secondary', 'success', 'warning', 'error', 'info'];
  const hash = categoryName.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  return colors[Math.abs(hash) % colors.length];
};

export const PersonalizedRecommendations: React.FC<PersonalizedRecommendationsProps> = ({
  recommendedBooks,
  recommendedResources
}) => {
  // Add fallback values to prevent undefined errors
  const safeRecommendedBooks = recommendedBooks || [];
  const safeRecommendedResources = recommendedResources || [];
  
  // State for book detail dialog
  const [bookDetailOpen, setBookDetailOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  
  // State for resource viewer dialog
  const [resourceViewerOpen, setResourceViewerOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<ResourceType | null>(null);
  const [loadingResource, setLoadingResource] = useState(false);
  
  // Convert dashboard Book to main Book type for BookDetail
  const convertBookForDetail = (book: Book): any => {
    return {
      id: book.id,
      title: book.title,
      author: book.author,
      isbn: book.isbn,
      publisher: book.publisher,
      categoryId: book.categoryId,
      category: book.category,
      subjects: book.subjects,
      summary: book.summary,
      notes: book.notes,
      isDeleted: book.isDeleted,
      dateCreated: book.dateCreated
    };
  };
  
  // Handle book detail view
  const handleViewBook = (book: Book) => {
    setSelectedBook(book);
    setBookDetailOpen(true);
  };
  
  // Handle resource view/download
  const handleViewResource = async (resource: Resource) => {
    try {
      setLoadingResource(true);
      // Fetch full resource details from API
      const fullResource = await resourceService.getResourceById(resource.id);
      setSelectedResource(fullResource);
      setResourceViewerOpen(true);
    } catch (error) {
      logger.error('Error loading resource:', error);
      // Fallback: try to convert dashboard resource to main resource type
      const convertedResource: ResourceType = {
        id: resource.id,
        title: resource.title,
        description: resource.description,
        type: resource.type,
        tags: resource.tags,
        filePath: resource.filePath,
        fileName: resource.fileName,
        fileType: resource.fileType,
        fileSize: resource.fileSize,
        uploadedByUserId: resource.uploadedByUserId,
        uploadedByUser: resource.uploadedByUser,
        uploadDate: resource.uploadDate,
        isDeleted: resource.isDeleted,
        dateCreated: resource.dateCreated,
        viewCount: 0,
        downloadCount: 0
      } as ResourceType;
      setSelectedResource(convertedResource);
      setResourceViewerOpen(true);
    } finally {
      setLoadingResource(false);
    }
  };

  return (
    <Box>
      {/* Recommended Books */}
      <Card sx={{ mb: 3 }}>
        <CardHeader 
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <StarIcon color="primary" />
              <Typography variant="h6">Sách Được Đề Xuất</Typography>
            </Box>
          }
        />
        <CardContent>
          {safeRecommendedBooks.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              Không có sách được đề xuất
            </Typography>
          ) : (
            <List>
              {safeRecommendedBooks.map((book, index) => (
                <React.Fragment key={book.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <BookIcon color="primary" />
                          <Typography variant="subtitle1" fontWeight="medium">
                            {book.title}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" mb={1}>
                            {book.author}
                          </Typography>
                          {book.summary && (
                            <Typography variant="body2" color="text.secondary" mb={1}>
                              {book.summary.length > 150 
                                ? `${book.summary.substring(0, 150)}...` 
                                : book.summary}
                            </Typography>
                          )}
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {book.category && (
                              <Chip
                                label={book.category.name}
                                size="small"
                                color={getCategoryColor(book.category.name) as any}
                                variant="outlined"
                              />
                            )}
                            {book.subjects?.slice(0, 2).map((subject, idx) => (
                              <Chip
                                key={idx}
                                label={subject}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={() => handleViewBook(book)}
                      >
                        Xem Chi Tiết
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < safeRecommendedBooks.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Recommended Resources */}
      <Card>
        <CardHeader 
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <TrendingUpIcon color="secondary" />
              <Typography variant="h6">Sách Số Được Đề Xuất (Cá Nhân)</Typography>
            </Box>
          }
        />
        <CardContent>
          {safeRecommendedResources.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              Không có tài nguyên được đề xuất
            </Typography>
          ) : (
            <List>
              {safeRecommendedResources.map((resource, index) => (
                <React.Fragment key={resource.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <ResourceIcon color="secondary" />
                          <Typography variant="subtitle1" fontWeight="medium">
                            {resource.title}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          {resource.description && (
                            <Typography variant="body2" color="text.secondary" mb={1}>
                              {resource.description.length > 150 
                                ? `${resource.description.substring(0, 150)}...` 
                                : resource.description}
                            </Typography>
                          )}
                          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                            <Chip
                              label={resource.type}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {resource.fileType} • {formatDate(resource.uploadDate)}
                            </Typography>
                            {resource.tags?.slice(0, 2).map((tag, idx) => (
                              <Chip
                                key={idx}
                                label={tag}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={() => handleViewResource(resource)}
                        disabled={loadingResource}
                      >
                        {loadingResource ? 'Đang tải...' : 'Xem/Tải Xuống'}
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < safeRecommendedResources.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      
      {/* Book Detail Dialog */}
      {selectedBook && (
        <BookDetail
          open={bookDetailOpen}
          onClose={() => {
            setBookDetailOpen(false);
            setSelectedBook(null);
          }}
          book={convertBookForDetail(selectedBook)}
        />
      )}
      
      {/* Resource Viewer Dialog */}
      {selectedResource && (
        <ResourceViewer
          resource={selectedResource}
          open={resourceViewerOpen}
          onClose={() => {
            setResourceViewerOpen(false);
            setSelectedResource(null);
          }}
        />
      )}
    </Box>
  );
};
